import type { JSX } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CompanyFormFields } from './CompanyFormFields';
import { CompanyLogoFields } from './CompanyLogoFields';
import { useCreateCompanyForm } from './CreateCompanyDialog.hooks';

interface CreateCompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function DialogButtons({
  isLoading,
  onCancel,
}: {
  isLoading: boolean;
  onCancel: () => void;
}): React.JSX.Element {
  return (
    <DialogFooter>
      <Button type="button" variant="outline" onClick={onCancel}>
        Cancel
      </Button>
      <Button type="submit" disabled={isLoading}>
        {isLoading ? 'Creating...' : 'Create'}
      </Button>
    </DialogFooter>
  );
}

export function CreateCompanyDialog({ open, onOpenChange }: CreateCompanyDialogProps): JSX.Element {
  const form = useCreateCompanyForm(onOpenChange);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-row-click-ignore
        className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]"
      >
        <DialogHeader>
          <DialogTitle>Create Company</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.onSubmit} className="space-y-4">
          <CompanyFormFields
            register={form.register}
            errors={form.errors}
            onSizeChange={(v) => form.setValue('companySize', v, { shouldDirty: true })}
            onCountryChange={(v) => form.setValue('country', v, { shouldDirty: true })}
            countryValue={form.watch('country') ?? ''}
            sizeValue={form.watch('companySize')}
          />
          <CompanyLogoFields
            logoFile={form.logoFile}
            darkLogoFile={form.darkLogoFile}
            onLogoChange={form.setLogoFile}
            onDarkLogoChange={form.setDarkLogoFile}
          />
          <DialogButtons isLoading={form.isLoading} onCancel={() => onOpenChange(false)} />
        </form>
      </DialogContent>
    </Dialog>
  );
}
